import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { BulkTransportGroups, OrderingInformation } from '@next/core-lib/cart';
import { AgrChipsFilterModule } from '@next/core-lib/components';
import { AgrFormModule } from '@next/core-lib/form';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { SelectedLocation, UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, scan, startWith, takeUntil, tap } from 'rxjs/operators';

import { decorateFormControl } from '../../helpers/add-to-cart-helpers';
import { QuantityItem } from '../../models/product.models';

@Component({
  selector: 'app-product-quantity',
  templateUrl: './product-quantity.component.html',
  styleUrls: ['./product-quantity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIcon,
    AgrChipsFilterModule,
    FormsModule,
    ReactiveFormsModule,
    MatButton,
    MatFormField,
    MatLabel,
    MatInput,
    AgrFormModule,
    MatSuffix,
    AsyncPipe,
    DecimalPipe,
    AgrI18nSharedModule,
  ],
})
export class ProductQuantityComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('productQuantity') productQuantity: ElementRef;

  @Output() quantityChange = new EventEmitter<number>();
  @Output() quantityIsValid = new EventEmitter<boolean>();

  @Input() quantityItem: QuantityItem;

  productQuantityControl = new FormControl();
  quickChoiceControl = new FormControl();

  showMoreToggle$ = new Subject<void>();
  shouldShowAllChips$: Observable<boolean>;
  selectedLocation: SelectedLocation;
  mobileShortChipCount = 5;
  shortChipCount = 15;

  isBulkProduct = false;
  quickChoiceItems: { name: string; selected: boolean; value: number }[] = [];

  readonly destroy$ = new Subject<void>();
  productQuantityMaxErrorMessage: string;

  private readonly quickChoiceItemsAmount = 12;

  constructor(
    private readonly localeService: LocaleService,
    private readonly decimalPipe: DecimalPipe,
    private readonly store: Store,
    private readonly changeDeterctorRef: ChangeDetectorRef,
    @Inject(BreakpointServiceInjector) private readonly breakpointService: BreakpointService,
  ) {}

  ngOnInit(): void {
    this.store.select(UserSelectors.selectLocation).subscribe((l) => (this.selectedLocation = l));

    this.productQuantityMaxErrorMessage = this.localeService.translate('product.wizard.quantity.quantity-error-max', {
      maxOrderQuantity: '' + this.quantityItem.orderingInformation.maximumOrderUnitQuantity,
      orderUnit: this.quantityItem.orderingInformation.orderUnit,
    });

    this.isBulkProduct = BulkTransportGroups.includes(this.quantityItem.orderingInformation.transportGroup);

    this.productQuantityControl.setValue(this.quantityItem.quantity);
    this.quickChoiceControl.setValue(this.quantityItem.quantity);

    this.shouldShowAllChips$ = this.showMoreToggle$.pipe(
      scan((acc) => !acc, false),
      startWith(false),
      takeUntil(this.destroy$),
    );

    this.breakpointService.ltSm$.pipe(takeUntil(this.destroy$)).subscribe((ltSm) => {
      combineLatest([this.shouldShowAllChips$])
        .pipe(takeUntil(this.destroy$))
        .subscribe(([shouldShowAllChips]) => {
          if (this.isBulkProduct) {
            this.quickChoiceItems = this.createQuickChoiceItemsForBulkProduct(
              this.productQuantityControl.value,
              ltSm,
              shouldShowAllChips,
              this.quantityItem.orderingInformation,
            );
          } else {
            this.quickChoiceItems = this.createQuickChoiceItems(
              this.productQuantityControl.value,
              this.quantityItem.orderingInformation,
              ltSm ? this.quickChoiceItemsAmount / 2 : this.quickChoiceItemsAmount,
            );
          }

          this.changeDeterctorRef.detectChanges();
        });
    });

    decorateFormControl(this.quantityItem, this.productQuantityControl, this.localeService);

    this.productQuantityControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        debounceTime(100),
        takeUntil(this.destroy$),
        tap(() => {
          this.quickChoiceControl.setValue(this.productQuantityControl.value, { emitEvent: false });
          this.productQuantity.nativeElement.focus();

          this.quantityChange.emit(this.productQuantityControl.value);
          this.quantityIsValid.emit(this.productQuantityControl.valid);
        }),
      )
      .subscribe();

    this.productQuantityControl.updateValueAndValidity();
    this.quantityIsValid.emit(this.productQuantityControl.valid);
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.productQuantity.nativeElement.focus();
    }, 0);
  }

  get totalOrderedWeightInKg(): number | null {
    if (this.quantityItem?.orderingInformation?.orderUnit !== 'KGD') {
      return null;
    }

    const conversionFactor = this.getKgdConversionFactor(this.quantityItem?.packagingInformation?.packageDescription);
    const quantity = this.productQuantityControl.value;

    if (conversionFactor === null || typeof quantity !== 'number') {
      return null;
    }

    return quantity * conversionFactor;
  }

  toggleShowMoreQuantityOptions() {
    this.showMoreToggle$.next();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleQuickChoiceChange(selected: number | number[]): void {
    const quantityExistsInQuickChoiceItems = this.quickChoiceItems.some((item) => item.value === this.productQuantityControl.value);

    if (selected !== null && selected !== this.productQuantityControl.value) {
      this.productQuantityControl.setValue(selected as number);
      return;
    }
    if (selected === null && quantityExistsInQuickChoiceItems) {
      this.productQuantityControl.setValue(selected as number);
    }
  }

  private getKgdConversionFactor(packageDescription: string | undefined): number | null {
    if (!packageDescription) {
      return null;
    }

    // Matches a numeric KG-per-KGD factor, allowing surrounding whitespace and either comma or point decimal separators.
    const factorMatch = /^\s*(\d+(?:[.,]\d+)?)\s*KG\s*$/i.exec(packageDescription);
    if (!factorMatch) {
      return null;
    }

    const conversionFactor = Number(factorMatch[1].replace(',', '.'));
    return conversionFactor > 0 ? conversionFactor : null;
  }

  private createQuickChoiceItems(value: number, orderingInformation: OrderingInformation, standardQuickChoiceItemsAmount: number): ProductQuantityChip[] {
    const { minimumOrderUnitQuantity, maximumOrderUnitQuantity, increaseOrderUnitQuantity, orderUnit, palletOrderUnitQuantity } = orderingInformation;
    if (!minimumOrderUnitQuantity || !increaseOrderUnitQuantity) {
      return [];
    }

    const maxQuickChoiceItemsAmount = !maximumOrderUnitQuantity
      ? standardQuickChoiceItemsAmount
      : Math.trunc((maximumOrderUnitQuantity - minimumOrderUnitQuantity) / increaseOrderUnitQuantity);

    let quickChoiceItems = Array.from(
      {
        length: Math.min(standardQuickChoiceItemsAmount, maxQuickChoiceItemsAmount),
      },
      (_, i) => {
        const chipValue = minimumOrderUnitQuantity + i * increaseOrderUnitQuantity;
        const chipName = `${this.decimalPipe.transform(chipValue)} ${this.localeService.translate('unit.' + orderUnit)}`;

        return {
          name: chipName,
          selected: chipValue === value,
          value: chipValue,
        };
      },
    );

    if (palletOrderUnitQuantity && minimumOrderUnitQuantity < palletOrderUnitQuantity) {
      quickChoiceItems = this.enrichWithPalletQuantityChips(quickChoiceItems, orderingInformation, standardQuickChoiceItemsAmount, value);
    }
    return quickChoiceItems;
  }

  private enrichWithPalletQuantityChips(
    quickChoiceItems: ProductQuantityChip[],
    orderingInformation: OrderingInformation,
    standardQuickChoiceItemsAmount: number,
    value: number,
  ): ProductQuantityChip[] {
    const { minimumOrderUnitQuantity, maximumOrderUnitQuantity, increaseOrderUnitQuantity, orderUnit, palletOrderUnitQuantity } = orderingInformation;

    const halfPalletQuantity = palletOrderUnitQuantity / 2;

    const validHalfPalletQuantity =
      ((halfPalletQuantity - minimumOrderUnitQuantity) % increaseOrderUnitQuantity === 0 && halfPalletQuantity >= minimumOrderUnitQuantity) ||
      halfPalletQuantity === maximumOrderUnitQuantity;
    const validPalletQuantity =
      ((palletOrderUnitQuantity - minimumOrderUnitQuantity) % increaseOrderUnitQuantity === 0 && palletOrderUnitQuantity >= minimumOrderUnitQuantity) ||
      palletOrderUnitQuantity === maximumOrderUnitQuantity;

    const halfPalletName = `${this.localeService.translate('product.wizard.quantity.half-pallet')} (${this.decimalPipe.transform(
      halfPalletQuantity,
    )} ${this.localeService.translate('unit.' + orderUnit)})`;
    const wholePalletName = `${this.localeService.translate('product.wizard.quantity.whole-pallet')} (${this.decimalPipe.transform(
      palletOrderUnitQuantity,
    )} ${this.localeService.translate('unit.' + orderUnit)})`;

    let quickChoiceItemsWithoutPalletQuantities = quickChoiceItems.filter(
      (chip) => chip.value !== halfPalletQuantity && chip.value !== palletOrderUnitQuantity,
    );

    const palletQuantityChips: ProductQuantityChip[] = [];

    if (validHalfPalletQuantity) {
      palletQuantityChips.push({
        name: halfPalletName,
        value: halfPalletQuantity,
        selected: halfPalletQuantity === value,
      });
    }

    if (validPalletQuantity) {
      palletQuantityChips.push({
        name: wholePalletName,
        value: palletOrderUnitQuantity,
        selected: palletOrderUnitQuantity === value,
      });
    }

    const totalChips = quickChoiceItemsWithoutPalletQuantities.length + palletQuantityChips.length;
    if (totalChips > standardQuickChoiceItemsAmount) {
      const excess = totalChips - standardQuickChoiceItemsAmount;
      quickChoiceItemsWithoutPalletQuantities = quickChoiceItems.slice(0, quickChoiceItemsWithoutPalletQuantities.length - excess);
    }

    return [...palletQuantityChips, ...quickChoiceItemsWithoutPalletQuantities];
  }

  private createQuickChoiceItemsForBulkProduct(
    selectedValue: number,
    isSmallScreen: boolean,
    shouldShowAllChips: boolean,
    { minimumOrderUnitQuantity, maximumOrderUnitQuantity, orderUnit, increaseOrderUnitQuantity }: OrderingInformation,
  ): ProductQuantityChip[] {
    let chips: ProductQuantityChip[] = [];

    const minQuantity = minimumOrderUnitQuantity ?? 1000;
    const maxQuantity = maximumOrderUnitQuantity ?? 32000;
    const quantityStepSize = increaseOrderUnitQuantity ?? 1000;

    for (let chipQuantity = minQuantity; chipQuantity < maxQuantity; chipQuantity += quantityStepSize) {
      chips.push({
        name: `${this.decimalPipe.transform(chipQuantity)} ${this.localeService.translate('unit.' + orderUnit)}`,
        selected: chipQuantity === selectedValue,
        value: chipQuantity,
      });
    }

    if (!shouldShowAllChips) {
      chips = isSmallScreen ? chips.slice(0, this.mobileShortChipCount - 1) : chips.slice(0, this.shortChipCount - 1);
    }

    chips.push({
      name: `${this.decimalPipe.transform(maxQuantity)} ${this.localeService.translate('unit.' + orderUnit)}`,
      selected: maxQuantity === selectedValue,
      value: maxQuantity,
    });

    return chips;
  }
}

interface ProductQuantityChip {
  name: string;
  selected: boolean;
  value: number;
}
